import { ComponentProps, ReactNode } from 'react'
import {
  CheckCircle2Icon,
  ChevronDownIcon,
  CircleIcon,
  XCircleIcon,
} from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { JsonCode } from './json-code'

/** 移植自 AI Elements tool.tsx,重写为单行高级卡片:
 *  折叠态一行展示 状态图标 + 名称 + 位置 + 结果预览 + 耗时;点击展开参数/结果。
 */
export type ToolCallStatus = 'running' | 'pending' | 'success' | 'error'

export type ToolProps = ComponentProps<typeof Collapsible>

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    className={cn(
      // doc 105:my-0 — 条目间距由外层时间线容器 gap 统一管理
      'group not-prose my-0 w-full rounded-lg border bg-background transition-colors',
      className,
    )}
    {...props}
  />
)

const statusLabels: Record<ToolCallStatus, string> = {
  running: '执行中',
  pending: '待执行',
  success: '成功',
  error: '失败',
}

/** 状态图标:running 为蓝色脉冲点,其余为小图标 */
const StatusIcon = ({ state }: { state: ToolCallStatus }) => {
  const inner =
    state === 'running' ? (
      <span className="relative flex size-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
        <span className="relative inline-flex size-2.5 rounded-full bg-blue-500" />
      </span>
    ) : state === 'success' ? (
      <CheckCircle2Icon className="size-3.5 text-green-500" />
    ) : state === 'error' ? (
      <XCircleIcon className="size-3.5 text-red-500" />
    ) : (
      <CircleIcon className="size-3.5 text-yellow-500" />
    )
  return (
    <span className="flex size-3.5 shrink-0 items-center justify-center" title={statusLabels[state]}>
      {inner}
    </span>
  )
}

export type ToolHeaderProps = {
  /** 工具名 */
  title: string
  state: ToolCallStatus
  /** 单行预览(结果拍平截断 / 执行中提示 / 错误文本;error 态自动标红) */
  preview?: ReactNode
  /** 已格式化的耗时,如 "340ms" / "1.2s" */
  duration?: string
  /** 位置胶囊等附加内容(紧跟名称后) */
  extra?: ReactNode
  /** 行尾操作(如 frontend 待执行的「执行」按钮) */
  action?: ReactNode
  className?: string
}

export const ToolHeader = ({
  className,
  title,
  state,
  preview,
  duration,
  extra,
  action,
}: ToolHeaderProps) => (
  <div
    className={cn(
      'flex w-full items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-muted/40',
      'group-data-[state=open]:rounded-b-none group-data-[state=open]:border-b group-data-[state=open]:hover:bg-transparent',
      className,
    )}
  >
    <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-2 text-left">
      <StatusIcon state={state} />
      <span className="shrink-0 font-mono text-[13px] font-medium">{title}</span>
      {extra}
      {preview != null && preview !== '' && (
        <span
          className={cn(
            'min-w-0 flex-1 truncate font-mono text-xs',
            state === 'error' ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {preview}
        </span>
      )}
    </CollapsibleTrigger>
    <div className="flex shrink-0 items-center gap-2">
      {duration && (
        <span className="text-[11px] tabular-nums text-muted-foreground">{duration}</span>
      )}
      {action}
      <CollapsibleTrigger aria-label="展开/折叠" className="rounded p-0.5 hover:bg-muted">
        <ChevronDownIcon className="size-3.5 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
    </div>
  </div>
)

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      'space-y-2.5 p-3 text-sm text-popover-foreground outline-none',
      'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2',
      'data-[state=open]:animate-in data-[state=open]:slide-in-from-top-2',
      className,
    )}
    {...props}
  />
)

export type ToolInputProps = ComponentProps<'div'> & {
  input: unknown
}

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => (
  <div className={cn('space-y-1.5 overflow-hidden', className)} {...props}>
    <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">参数</h4>
    <JsonCode code={typeof input === 'string' ? input : JSON.stringify(input, null, 2)} />
  </div>
)

export type ToolOutputProps = ComponentProps<'div'> & {
  output?: string
  errorText?: string
}

export const ToolOutput = ({ className, output, errorText, ...props }: ToolOutputProps) => {
  if (!(output || errorText)) return null

  return (
    <div className={cn('space-y-1.5', className)} {...props}>
      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {errorText ? '错误' : '结果'}
      </h4>
      {errorText ? (
        <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive whitespace-pre-wrap break-all">
          {errorText}
        </div>
      ) : (
        <JsonCode code={output ?? ''} />
      )}
    </div>
  )
}
